import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users, BookOpen, ShoppingCart, Settings, LogOut,
  Plus, Trash2, Edit, Eye, Search, RefreshCw,
  ChevronDown, ChevronUp, Download, Shield, ShieldOff,
  Database, Activity, DollarSign, BarChart3, Upload,
  X, Check, FileText, Image, ToggleLeft, ToggleRight,
  Lock, AlertTriangle, FileDown, FileUp, CheckCircle, XCircle,
  Mail, UserX, CreditCard, Clock, CheckCheck, XOctagon, UploadCloud, TrendingUp
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import RestoreBackupModal from './RestoreBackupModal';
import AnalyticsDashboard from './AnalyticsDashboard';
import BookBuyersModal from './BookBuyersModal';
import UserDetailModal from './UserDetailModal';

const BACKEND_URL = window.location.origin;

const AdminPanel = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [users, setUsers] = useState([]);
  const [books, setBooks] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteConfirmModal, setDeleteConfirmModal] = useState({ show: false, userId: null, userName: '' });
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [importModal, setImportModal] = useState({ show: false, results: null, importing: false });
  const [exportOptions, setExportOptions] = useState({ show: false });
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [buyersModal, setBuyersModal] = useState({ show: false, bookId: null, bookTitle: '' });
  const [editBook, setEditBook] = useState(null);
  const [accessModal, setAccessModal] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    verifyAdmin();
  }, []);

  useEffect(() => {
    if (activeTab === 'dashboard') fetchStats();
    if (activeTab === 'users') { fetchUsers(); fetchBooks(); }
    if (activeTab === 'books') fetchBooks();
    if (activeTab === 'sessions') fetchSessions();
  }, [activeTab]);

  const verifyAdmin = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/verify`, {
        credentials: 'include'
      });
      if (!response.ok) {
        navigate('/ctrl-9x7k2m-auth-8f3j5n-secure-4w6r9t');
      }
      setLoading(false);
    } catch (error) {
      navigate('/ctrl-9x7k2m-auth-8f3j5n-secure-4w6r9t');
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/stats`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/users`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const fetchBooks = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/books/admin/all`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setBooks(data.books || []);
      }
    } catch (error) {
      console.error('Failed to fetch books:', error);
    }
  };

  const fetchSessions = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/sessions`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setSessions(data.sessions || []);
      }
    } catch (error) {
      console.error('Failed to fetch sessions:', error);
    }
  };

  const downloadFullBackup = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/backup/full`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        const timestamp = new Date().toISOString().split('T')[0];
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `nawwaf_alshibli_full_backup_${timestamp}.json`;
        link.click();
        URL.revokeObjectURL(link.href);
        alert(`✅ Full backup downloaded!\n\nIncludes:\n- ${data.statistics.total_users} Users\n- ${data.statistics.total_books} Books\n- ${data.statistics.total_purchases} Purchases\n- ${data.statistics.pending_purchases} Pending Purchases\n- ${data.statistics.active_sessions} Sessions\n- $${data.statistics.total_revenue} Total Revenue`);
      } else {
        alert('❌ Failed to create backup');
      }
    } catch (error) {
      console.error('Failed to download backup:', error);
      alert('❌ Failed to download backup');
    }
  };

  const handleLogout = async () => {
    await fetch(`${BACKEND_URL}/api/admin/logout`, {
      method: 'POST',
      credentials: 'include'
    });
    navigate('/ctrl-9x7k2m-auth-8f3j5n-secure-4w6r9t');
  };

  const openDeleteConfirm = (userId, userName) => {
    setDeleteConfirmModal({ show: true, userId, userName });
    setDeletePassword('');
    setDeleteError('');
  };

  const closeDeleteConfirm = () => {
    setDeleteConfirmModal({ show: false, userId: null, userName: '' });
    setDeletePassword('');
    setDeleteError('');
  };

  const deleteUser = async () => {
    // Verify password first
    if (deletePassword !== 'chatisgod-1990') {
      setDeleteError('Incorrect password. User deletion cancelled.');
      return;
    }

    try {
      await fetch(`${BACKEND_URL}/api/admin/users/${deleteConfirmModal.userId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      fetchUsers();
      closeDeleteConfirm();
    } catch (error) {
      console.error('Failed to delete user:', error);
      setDeleteError('Failed to delete user. Please try again.');
    }
  };

  // ==================== CSV EXPORT FUNCTIONS ====================
  
  const exportUsersCSV = (format = 'full') => {
    if (users.length === 0) {
      alert('No users to export');
      return;
    }

    let headers, rows;
    const timestamp = new Date().toISOString().split('T')[0];

    if (format === 'full') {
      // Full export with all fields
      headers = ['user_id', 'email', 'name', 'picture', 'has_access', 'role', 'favorites', 'purchased_books', 'created_at', 'updated_at'];
      rows = users.map(user => [
        user.user_id || '',
        user.email || '',
        user.name || '',
        user.picture || '',
        user.has_access !== false ? 'true' : 'false',
        user.role || 'user',
        JSON.stringify(user.favorites || []),
        JSON.stringify(user.purchased_books || []),
        user.created_at || '',
        user.updated_at || ''
      ]);
    } else if (format === 'basic') {
      // Basic export - just essential info
      headers = ['email', 'name', 'has_access', 'created_at'];
      rows = users.map(user => [
        user.email || '',
        user.name || '',
        user.has_access !== false ? 'true' : 'false',
        user.created_at || ''
      ]);
    } else if (format === 'email_only') {
      // Email list only
      headers = ['email', 'name'];
      rows = users.map(user => [
        user.email || '',
        user.name || ''
      ]);
    }

    // Create CSV content
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    // Download file
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `users_${format}_${timestamp}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
    
    setExportOptions({ show: false });
  };

  const exportUsersJSON = () => {
    if (users.length === 0) {
      alert('No users to export');
      return;
    }

    const timestamp = new Date().toISOString().split('T')[0];
    const exportData = {
      exported_at: new Date().toISOString(),
      total_users: users.length,
      users: users.map(user => ({
        user_id: user.user_id,
        email: user.email,
        name: user.name,
        picture: user.picture,
        has_access: user.has_access !== false,
        role: user.role || 'user',
        favorites: user.favorites || [],
        purchased_books: user.purchased_books || [],
        created_at: user.created_at,
        updated_at: user.updated_at
      }))
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `users_backup_${timestamp}.json`;
    link.click();
    URL.revokeObjectURL(link.href);
    
    setExportOptions({ show: false });
  };

  // ==================== CSV IMPORT FUNCTIONS ====================
  
  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const fileType = file.name.split('.').pop().toLowerCase();
    
    if (fileType === 'csv') {
      await importCSV(file);
    } else if (fileType === 'json') {
      await importJSON(file);
    } else {
      alert('Please select a CSV or JSON file');
    }
    
    // Reset file input
    event.target.value = '';
  };

  const importCSV = async (file) => {
    setImportModal({ show: true, results: null, importing: true });
    
    try {
      const text = await file.text();
      const lines = text.split('\n').filter(line => line.trim());
      
      if (lines.length < 2) {
        setImportModal({ show: true, results: { error: 'CSV file is empty or has no data rows' }, importing: false });
        return;
      }

      // Parse headers
      const headers = parseCSVLine(lines[0]).map(h => h.toLowerCase().trim());
      const emailIndex = headers.indexOf('email');
      const nameIndex = headers.indexOf('name');
      
      if (emailIndex === -1) {
        setImportModal({ show: true, results: { error: 'CSV must have an "email" column' }, importing: false });
        return;
      }

      // Parse data rows
      const results = { success: [], failed: [], skipped: [] };
      
      for (let i = 1; i < lines.length; i++) {
        const values = parseCSVLine(lines[i]);
        const email = values[emailIndex]?.trim();
        const name = nameIndex !== -1 ? values[nameIndex]?.trim() : email?.split('@')[0];
        
        if (!email || !email.includes('@')) {
          results.skipped.push({ row: i + 1, reason: 'Invalid or missing email' });
          continue;
        }

        // Check if user already exists
        const existingUser = users.find(u => u.email.toLowerCase() === email.toLowerCase());
        if (existingUser) {
          results.skipped.push({ row: i + 1, email, reason: 'User already exists' });
          continue;
        }

        // Create user via API
        try {
          const response = await fetch(`${BACKEND_URL}/api/admin/users`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
              email,
              name: name || email.split('@')[0],
              role: 'user',
              has_access: true
            })
          });

          if (response.ok) {
            results.success.push({ row: i + 1, email, name });
          } else {
            const error = await response.json();
            results.failed.push({ row: i + 1, email, reason: error.detail || 'API error' });
          }
        } catch (err) {
          results.failed.push({ row: i + 1, email, reason: 'Network error' });
        }
      }

      setImportModal({ show: true, results, importing: false });
      fetchUsers(); // Refresh users list
      
    } catch (error) {
      setImportModal({ show: true, results: { error: `Failed to parse CSV: ${error.message}` }, importing: false });
    }
  };

  const importJSON = async (file) => {
    setImportModal({ show: true, results: null, importing: true });
    
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      const usersToImport = data.users || (Array.isArray(data) ? data : [data]);
      
      if (usersToImport.length === 0) {
        setImportModal({ show: true, results: { error: 'No users found in JSON file' }, importing: false });
        return;
      }

      const results = { success: [], failed: [], skipped: [] };

      for (let i = 0; i < usersToImport.length; i++) {
        const user = usersToImport[i];
        const email = user.email?.trim();
        const name = user.name?.trim() || email?.split('@')[0];

        if (!email || !email.includes('@')) {
          results.skipped.push({ row: i + 1, reason: 'Invalid or missing email' });
          continue;
        }

        const existingUser = users.find(u => u.email.toLowerCase() === email.toLowerCase());
        if (existingUser) {
          results.skipped.push({ row: i + 1, email, reason: 'User already exists' });
          continue;
        }

        try {
          const response = await fetch(`${BACKEND_URL}/api/admin/users`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
              email,
              name,
              role: user.role || 'user',
              has_access: user.has_access !== false
            })
          });

          if (response.ok) {
            results.success.push({ row: i + 1, email, name });
          } else {
            const error = await response.json();
            results.failed.push({ row: i + 1, email, reason: error.detail || 'API error' });
          }
        } catch (err) {
          results.failed.push({ row: i + 1, email, reason: 'Network error' });
        }
      }

      setImportModal({ show: true, results, importing: false });
      fetchUsers();
      
    } catch (error) {
      setImportModal({ show: true, results: { error: `Failed to parse JSON: ${error.message}` }, importing: false });
    }
  };

  // Helper function to parse CSV line (handles quoted values)
  const parseCSVLine = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      
      if (char === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    result.push(current.trim());
    return result;
  };

  const toggleUserAccess = async (userId, hasAccess) => {
    const endpoint = hasAccess ? 'revoke-access' : 'grant-access';
    try {
      await fetch(`${BACKEND_URL}/api/admin/users/${userId}/${endpoint}`, {
        method: 'POST',
        credentials: 'include'
      });
      fetchUsers();
    } catch (error) {
      console.error('Failed to toggle access:', error);
    }
  };

  const sendWelcomeEmail = async (userId, userName) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/users/${userId}/send-welcome-email`, {
        method: 'POST',
        credentials: 'include'
      });
      const data = await response.json();
      if (response.ok) {
        alert(`✅ Welcome email sent to ${userName}!`);
      } else {
        alert(`❌ Failed: ${data.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Failed to send email:', error);
      alert('❌ Failed to send email. Check console for details.');
    }
  };

  const clearAllUsers = async () => {
    const confirmed = window.prompt('Type "DELETE ALL" to confirm clearing all users:');
    if (confirmed !== 'DELETE ALL') {
      alert('Cancelled. You must type exactly "DELETE ALL" to confirm.');
      return;
    }
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/users/clear-all`, {
        method: 'DELETE',
        credentials: 'include'
      });
      const data = await response.json();
      if (response.ok) {
        alert(`✅ Cleared ${data.users_deleted} users and ${data.sessions_deleted} sessions`);
        fetchUsers();
        fetchStats();
      } else {
        alert(`❌ Failed: ${data.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Failed to clear users:', error);
      alert('❌ Failed to clear users');
    }
  };

  const deleteBook = async (bookId) => {
    const pwd = window.prompt('Enter admin password to delete this book:');
    if (!pwd) return;
    if (pwd !== 'chatisgod-1990') {
      alert('Incorrect password. Deletion cancelled.');
      return;
    }
    if (!window.confirm('Are you sure? This cannot be undone.')) return;
    try {
      await fetch(`${BACKEND_URL}/api/books/${bookId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      fetchBooks();
    } catch (error) {
      console.error('Failed to delete book:', error);
    }
  };

  const updateBook = () => {
    // EditBookModal now performs the PUT itself (with progress). We just refresh + close.
    fetchBooks();
    setEditBook(null);
  };

  const deleteSession = async (sessionToken) => {
    try {
      await fetch(`${BACKEND_URL}/api/admin/sessions/${sessionToken}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      fetchSessions();
    } catch (error) {
      console.error('Failed to delete session:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-pink-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const filteredUsers = users.filter(user =>
    user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.user_id?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
    { id: 'analytics', label: 'Analytics', icon: TrendingUp },
    { id: 'purchases', label: 'Purchases', icon: DollarSign },
    { id: 'users', label: 'Users', icon: Users },
    { id: 'books', label: 'Books', icon: BookOpen },
    { id: 'sessions', label: 'Sessions', icon: Activity },
    { id: 'upload', label: 'Upload Book', icon: Upload },
    { id: 'settings', label: 'Site Settings', icon: Settings },
  ];

  return (
    <div className="min-h-screen bg-gray-900 flex">
      {/* Sidebar */}
      <div className="w-64 bg-gray-800 border-r border-gray-700 flex flex-col">
        <div className="p-6 border-b border-gray-700">
          <h1 className="text-xl font-bold text-white flex items-center gap-2">
            <Database className="w-6 h-6 text-pink-500" />
            Admin Panel
          </h1>
        </div>
        
        <nav className="flex-1 p-4">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`w-full flex items-center justify-between px-4 py-3 rounded-lg mb-2 transition-all ${
                activeTab === tab.id
                  ? 'bg-pink-600 text-white'
                  : 'text-gray-400 hover:bg-gray-700 hover:text-white'
              }`}
            >
              <div className="flex items-center gap-3">
                <tab.icon className="w-5 h-5" />
                {tab.label}
              </div>
              {tab.badge > 0 && (
                <span className="bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                  {tab.badge}
                </span>
              )}
            </button>
          ))}
        </nav>

        <div className="p-4 border-t border-gray-700">
          <Button
            onClick={handleLogout}
            variant="ghost"
            className="w-full justify-start text-red-400 hover:text-red-300 hover:bg-red-500/10"
          >
            <LogOut className="w-5 h-5 mr-3" />
            Logout
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto">
        <div className="p-8">
          {/* Dashboard */}
          {activeTab === 'dashboard' && (
            <div>
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-white">Dashboard</h2>
                <div className="flex gap-3">
                  <Button onClick={() => setShowRestoreModal(true)} variant="outline" className="border-purple-600 text-purple-400 hover:bg-purple-600/20">
                    <UploadCloud className="w-4 h-4 mr-2" />
                    Restore Backup
                  </Button>
                  <Button onClick={downloadFullBackup} className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700">
                    <Database className="w-4 h-4 mr-2" />
                    Full Database Backup (JSON)
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <StatCard icon={Users} label="Total Users" value={stats.total_users || 0} color="blue" />
                <StatCard icon={Activity} label="Active Sessions" value={stats.active_sessions || 0} color="green" />
                <StatCard icon={BookOpen} label="Total Books" value={stats.total_books || 0} color="purple" />
                <StatCard icon={DollarSign} label="Revenue" value={`$${stats.total_revenue || 0}`} color="pink" />
              </div>

              <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
                <h3 className="text-lg font-semibold text-white mb-4">Recent Users</h3>
                <div className="space-y-3">
                  {(stats.recent_users || []).map((user) => (
                    <div key={user.user_id} className="flex items-center justify-between p-3 bg-gray-700/50 rounded-lg">
                      <div className="flex items-center gap-3">
                        {user.picture ? (
                          <img src={user.picture} alt="" className="w-10 h-10 rounded-full" />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-gray-600 flex items-center justify-center">
                            <Users className="w-5 h-5 text-gray-400" />
                          </div>
                        )}
                        <div>
                          <p className="text-white font-medium">{user.name}</p>
                          <p className="text-gray-400 text-sm">{user.email}</p>
                        </div>
                      </div>
                      <span className="text-gray-400 text-sm">
                        {new Date(user.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Analytics Dashboard */}
          {activeTab === 'analytics' && (
            <AnalyticsDashboard />
          )}

          {/* Purchases - full report */}
          {activeTab === 'purchases' && (
            <PurchasesTab />
          )}

          {/* Users */}
          {activeTab === 'users' && (
            <div>
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-white">Users ({users.length})</h2>
                <div className="flex gap-3">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      placeholder="Search users..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 bg-gray-800 border-gray-700 text-white w-48"
                    />
                  </div>
                  <input type="file" ref={fileInputRef} onChange={handleFileSelect} accept=".csv,.json" className="hidden" />
                  <Button onClick={handleImportClick} variant="outline" className="border-green-600 text-green-400 hover:bg-green-600/20">
                    <FileUp className="w-4 h-4 mr-2" />Import
                  </Button>
                  <div className="relative">
                    <Button onClick={() => setExportOptions({ show: !exportOptions.show })} variant="outline" className="border-blue-600 text-blue-400 hover:bg-blue-600/20">
                      <FileDown className="w-4 h-4 mr-2" />Export<ChevronDown className="w-4 h-4 ml-1" />
                    </Button>
                    {exportOptions.show && (
                      <div className="absolute right-0 top-full mt-2 w-56 bg-gray-800 border border-gray-700 rounded-lg shadow-xl z-20">
                        <div className="p-2">
                          <p className="text-xs text-gray-400 px-3 py-2 font-medium">CSV FORMATS</p>
                          <button onClick={() => exportUsersCSV('full')} className="w-full text-left px-3 py-2 text-gray-300 hover:bg-gray-700 rounded-lg text-sm">Full Export (All Fields)</button>
                          <button onClick={() => exportUsersCSV('basic')} className="w-full text-left px-3 py-2 text-gray-300 hover:bg-gray-700 rounded-lg text-sm">Basic (Email, Name, Access)</button>
                          <button onClick={() => exportUsersCSV('email_only')} className="w-full text-left px-3 py-2 text-gray-300 hover:bg-gray-700 rounded-lg text-sm">Email List Only</button>
                          <hr className="border-gray-700 my-2" />
                          <p className="text-xs text-gray-400 px-3 py-2 font-medium">JSON FORMAT</p>
                          <button onClick={exportUsersJSON} className="w-full text-left px-3 py-2 text-gray-300 hover:bg-gray-700 rounded-lg text-sm">Full Backup (JSON)</button>
                        </div>
                      </div>
                    )}
                  </div>
                  <Button onClick={clearAllUsers} variant="outline" className="border-red-600 text-red-400 hover:bg-red-600/20">
                    <UserX className="w-4 h-4 mr-2" />Clear All
                  </Button>
                  <Button onClick={fetchUsers} variant="outline" className="border-gray-700 text-gray-300">
                    <RefreshCw className="w-4 h-4 mr-2" />Refresh
                  </Button>
                </div>
              </div>

              <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-700/50">
                    <tr>
                      <th className="text-left px-6 py-4 text-gray-300 font-medium">User</th>
                      <th className="text-left px-6 py-4 text-gray-300 font-medium">ID</th>
                      <th className="text-left px-6 py-4 text-gray-300 font-medium">Email</th>
                      <th className="text-left px-6 py-4 text-gray-300 font-medium">Access</th>
                      <th className="text-left px-6 py-4 text-gray-300 font-medium">Created</th>
                      <th className="text-left px-6 py-4 text-gray-300 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map((user) => (
                      <tr key={user.user_id} className="border-t border-gray-700 hover:bg-gray-700/30">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            {user.picture ? (
                              <img src={user.picture} alt="" className="w-10 h-10 rounded-full" />
                            ) : (
                              <div className="w-10 h-10 rounded-full bg-gray-600 flex items-center justify-center">
                                <Users className="w-5 h-5 text-gray-400" />
                              </div>
                            )}
                            <span className="text-white">{user.name || 'No name'}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-gray-400 font-mono text-sm">{user.user_id}</td>
                        <td className="px-6 py-4 text-gray-300">{user.email}</td>
                        <td className="px-6 py-4">
                          <button
                            onClick={() => toggleUserAccess(user.user_id, user.has_access)}
                            className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm ${
                              user.has_access !== false
                                ? 'bg-green-500/20 text-green-400'
                                : 'bg-red-500/20 text-red-400'
                            }`}
                          >
                            {user.has_access !== false ? (
                              <><Shield className="w-4 h-4" /> Granted</>
                            ) : (
                              <><ShieldOff className="w-4 h-4" /> Revoked</>
                            )}
                          </button>
                        </td>
                        <td className="px-6 py-4 text-gray-400 text-sm">
                          {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex gap-2">
                            <button
                              onClick={() => setSelectedUserId(user.user_id)}
                              className="px-3 py-2 text-pink-400 hover:bg-pink-500/20 rounded-lg text-sm font-medium flex items-center gap-1"
                              title="Manage user — view activity & control purchases"
                              data-testid={`manage-user-${user.user_id}`}
                            >
                              <Eye className="w-4 h-4" /> Manage
                            </button>
                            <button
                              onClick={() => sendWelcomeEmail(user.user_id, user.name || user.email)}
                              className="p-2 text-blue-400 hover:bg-blue-500/20 rounded-lg"
                              title="Send welcome email"
                            >
                              <Mail className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => openDeleteConfirm(user.user_id, user.name || user.email)}
                              className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg"
                              title="Delete user"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Books */}
          {activeTab === 'books' && (
            <div>
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-white">Books ({books.length})</h2>
                <Button onClick={() => setActiveTab('upload')} className="bg-pink-600 hover:bg-pink-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Book
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {books.map((book) => (
                  <div key={book.book_id} className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
                    <div className="aspect-[3/4] bg-gray-700 relative">
                      {book.cover_url ? (
                        <img src={`${BACKEND_URL}${book.cover_url}`} alt={book.title} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <BookOpen className="w-16 h-16 text-gray-500" />
                        </div>
                      )}
                      <div className="absolute top-2 right-2 flex gap-1">
                        {book.is_free ? (
                          <span className="bg-green-500 text-white text-xs px-2 py-1 rounded">Free</span>
                        ) : (
                          <span className="bg-pink-500 text-white text-xs px-2 py-1 rounded">${book.price}</span>
                        )}
                      </div>
                    </div>
                    <div className="p-4">
                      <h3 className="text-white font-semibold mb-1">{book.title}</h3>
                      <p className="text-gray-400 text-sm mb-2">{book.author}</p>
                      <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
                        <span><Eye className="w-4 h-4 inline mr-1" />{book.views || 0}</span>
                        <span><Download className="w-4 h-4 inline mr-1" />{book.downloads || 0}</span>
                        <button 
                          onClick={() => setBuyersModal({ show: true, bookId: book.book_id, bookTitle: book.title })}
                          className="text-green-400 hover:text-green-300 hover:underline cursor-pointer"
                        >
                          <ShoppingCart className="w-4 h-4 inline mr-1" />{book.copies_sold || 0} sold
                        </button>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" className="flex-1 border-gray-600 text-gray-300" onClick={() => setEditBook(book)}>
                          <Edit className="w-4 h-4 mr-1" />Edit
                        </Button>
                        <Button variant="outline" size="sm" className="flex-1 border-gray-600 text-gray-300" onClick={() => setAccessModal(book)}>
                          <Users className="w-4 h-4 mr-1" />Access
                        </Button>
                        <Button variant="outline" size="sm" className="border-gray-600 text-gray-300" onClick={() => deleteBook(book.book_id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Sessions */}
          {activeTab === 'sessions' && (
            <div>
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-white">Active Sessions ({sessions.length})</h2>
                <Button onClick={fetchSessions} variant="outline" className="border-gray-700 text-gray-300">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh
                </Button>
              </div>

              <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-700/50">
                    <tr>
                      <th className="text-left px-6 py-4 text-gray-300 font-medium">User ID</th>
                      <th className="text-left px-6 py-4 text-gray-300 font-medium">Session Token</th>
                      <th className="text-left px-6 py-4 text-gray-300 font-medium">Expires</th>
                      <th className="text-left px-6 py-4 text-gray-300 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sessions.map((session, idx) => (
                      <tr key={idx} className="border-t border-gray-700 hover:bg-gray-700/30">
                        <td className="px-6 py-4 text-gray-300 font-mono text-sm">{session.user_id}</td>
                        <td className="px-6 py-4 text-gray-400 font-mono text-sm">
                          {session.session_token?.substring(0, 20)}...
                        </td>
                        <td className="px-6 py-4 text-gray-400 text-sm">
                          {session.expires_at ? new Date(session.expires_at).toLocaleString() : 'N/A'}
                        </td>
                        <td className="px-6 py-4">
                          <button
                            onClick={() => deleteSession(session.session_token)}
                            className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Upload Book */}
          {activeTab === 'upload' && (
            <BookUploadForm onSuccess={() => { fetchBooks(); setActiveTab('books'); }} />
          )}

          {activeTab === 'settings' && (
            <SiteSettingsPanel />
          )}
        </div>
      </div>

      {/* Delete User Confirmation Modal */}
      {deleteConfirmModal.show && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={closeDeleteConfirm} />
          <div className="relative bg-gray-800 rounded-2xl p-6 max-w-md w-full border border-gray-700 shadow-2xl">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertTriangle className="w-8 h-8 text-red-500" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Confirm User Deletion</h3>
              <p className="text-gray-400">
                You are about to delete <span className="text-white font-medium">{deleteConfirmModal.userName}</span>
              </p>
              <p className="text-red-400 text-sm mt-2">This action cannot be undone!</p>
            </div>

            <div className="mb-6">
              <label className="block text-gray-300 mb-2 text-sm">
                <Lock className="w-4 h-4 inline mr-2" />
                Enter admin password to confirm
              </label>
              <Input
                type="password"
                value={deletePassword}
                onChange={(e) => {
                  setDeletePassword(e.target.value);
                  setDeleteError('');
                }}
                placeholder="Enter admin password"
                className="bg-gray-700 border-gray-600 text-white"
              />
              {deleteError && (
                <p className="text-red-400 text-sm mt-2">{deleteError}</p>
              )}
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={closeDeleteConfirm}
                className="flex-1 border-gray-600 text-gray-300 hover:bg-gray-700"
              >
                Cancel
              </Button>
              <Button
                onClick={deleteUser}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete User
              </Button>
            </div>
          </div>
        </div>
      )}

      {importModal.show && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70" onClick={() => !importModal.importing && setImportModal({ show: false, results: null, importing: false })} />
          <div className="relative bg-gray-800 rounded-2xl p-6 max-w-lg w-full border border-gray-700">
            {importModal.importing ? (
              <div className="text-center py-8">
                <div className="w-12 h-12 border-4 border-pink-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                <p className="text-white text-lg">Importing users...</p>
              </div>
            ) : importModal.results?.error ? (
              <div className="text-center py-4">
                <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-white mb-2">Import Failed</h3>
                <p className="text-red-400">{importModal.results.error}</p>
                <Button onClick={() => setImportModal({ show: false, results: null, importing: false })} className="mt-6 bg-gray-700">Close</Button>
              </div>
            ) : (
              <div>
                <div className="text-center mb-6">
                  <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-white">Import Complete</h3>
                </div>
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-green-400">{importModal.results?.success?.length || 0}</p>
                    <p className="text-xs text-gray-400">Imported</p>
                  </div>
                  <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-yellow-400">{importModal.results?.skipped?.length || 0}</p>
                    <p className="text-xs text-gray-400">Skipped</p>
                  </div>
                  <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-red-400">{importModal.results?.failed?.length || 0}</p>
                    <p className="text-xs text-gray-400">Failed</p>
                  </div>
                </div>
                <Button onClick={() => setImportModal({ show: false, results: null, importing: false })} className="w-full bg-pink-600">Done</Button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Restore Backup Modal */}
      <RestoreBackupModal
        isOpen={showRestoreModal}
        onClose={() => setShowRestoreModal(false)}
        onSuccess={() => {
          fetchUsers();
          fetchBooks();
          fetchStats();
        }}
      />

      {/* Book Buyers Modal */}
      <BookBuyersModal
        isOpen={buyersModal.show}
        onClose={() => setBuyersModal({ show: false, bookId: null, bookTitle: '' })}
        bookId={buyersModal.bookId}
        bookTitle={buyersModal.bookTitle}
      />

      {editBook && (
        <EditBookModal
          book={editBook}
          onClose={() => setEditBook(null)}
          onSave={updateBook}
        />
      )}

      {accessModal && (
        <ManageAccessModal
          book={accessModal}
          allUsers={users}
          onClose={() => setAccessModal(null)}
        />
      )}

      {selectedUserId && (
        <UserDetailModal
          userId={selectedUserId}
          allBooks={books}
          onClose={() => setSelectedUserId(null)}
          onChanged={() => { fetchUsers(); fetchStats(); }}
        />
      )}
    </div>
  );
};

// Site Settings Panel
const SiteSettingsPanel = () => {
  const [welcomeVideo, setWelcomeVideo] = useState('');
  const [savedVideo, setSavedVideo] = useState('');
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/admin/settings/welcome_video`)
      .then(r => r.json())
      .then(d => { setWelcomeVideo(d.value || ''); setSavedVideo(d.value || ''); setLoaded(true); })
      .catch(() => setLoaded(true));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await fetch(`${BACKEND_URL}/api/admin/settings/welcome_video`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ value: welcomeVideo })
      });
      setSavedVideo(welcomeVideo);
    } catch (err) {
      console.error('Failed to save setting:', err);
    }
    setSaving(false);
  };

  const hasChanges = welcomeVideo !== savedVideo;

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-white">Site Settings</h2>

      <div className="bg-gray-800 rounded-xl border border-gray-700 p-6">
        <h3 className="text-lg font-semibold text-white mb-1">Welcome Video</h3>
        <p className="text-gray-400 text-sm mb-4">Set the video URL displayed in the Welcome section on the homepage. Supports YouTube, Vimeo, Google Drive, and any direct embed URL.</p>

        <div className="space-y-4">
          <div>
            <label className="block text-gray-300 mb-1 text-sm">Video URL</label>
            <Input
              type="url"
              placeholder="https://www.youtube.com/watch?v=..."
              value={welcomeVideo}
              onChange={(e) => setWelcomeVideo(e.target.value)}
              className="bg-gray-700 border-gray-600 text-white"
              data-testid="settings-welcome-video-input"
            />
            <div className="flex flex-wrap gap-2 mt-2">
              <span className="text-xs bg-red-500/20 text-red-300 px-2 py-0.5 rounded">YouTube</span>
              <span className="text-xs bg-blue-500/20 text-blue-300 px-2 py-0.5 rounded">Vimeo</span>
              <span className="text-xs bg-green-500/20 text-green-300 px-2 py-0.5 rounded">Google Drive</span>
              <span className="text-xs bg-purple-500/20 text-purple-300 px-2 py-0.5 rounded">Direct Embed</span>
            </div>
          </div>

          {welcomeVideo && (
            <div>
              <label className="block text-gray-300 mb-1 text-sm">Preview</label>
              <div className="rounded-lg overflow-hidden aspect-video bg-black border border-gray-700">
                <iframe
                  src={(() => {
                    const url = welcomeVideo;
                    const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
                    if (yt) return `https://www.youtube.com/embed/${yt[1]}?rel=0`;
                    const vim = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
                    if (vim) return `https://player.vimeo.com/video/${vim[1]}`;
                    const drv = url.match(/drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)/);
                    if (drv) return `https://drive.google.com/file/d/${drv[1]}/preview`;
                    return url;
                  })()}
                  title="Video Preview"
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  frameBorder="0"
                />
              </div>
            </div>
          )}

          <Button
            onClick={handleSave}
            disabled={saving || !hasChanges}
            className="bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-700 hover:to-purple-700 text-white disabled:opacity-50"
            data-testid="settings-save-btn"
          >
            {saving ? 'Saving...' : hasChanges ? 'Save Changes' : 'Saved'}
          </Button>
        </div>
      </div>
    </div>
  );
};

// Stat Card Component
const StatCard = ({ icon: Icon, label, value, color }) => {
  const colors = {
    blue: 'from-blue-500 to-blue-600',
    green: 'from-green-500 to-green-600',
    purple: 'from-purple-500 to-purple-600',
    pink: 'from-pink-500 to-pink-600'
  };

  return (
    <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
      <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${colors[color]} flex items-center justify-center mb-4`}>
        <Icon className="w-6 h-6 text-white" />
      </div>
      <p className="text-gray-400 text-sm">{label}</p>
      <p className="text-2xl font-bold text-white">{value}</p>
    </div>
  );
};

// Manage Access Modal
const ManageAccessModal = ({ book, allUsers, onClose }) => {
  const [usersWithAccess, setUsersWithAccess] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [granting, setGranting] = useState(null);

  // Self-load users so the modal works even if parent's `users` state is empty
  // (root cause of "users don't load all the time")
  const [usersList, setUsersList] = useState(allUsers || []);
  const [usersLoading, setUsersLoading] = useState(false);

  // Direct-by-email fallback
  const [emailInput, setEmailInput] = useState('');
  const [emailGrantBusy, setEmailGrantBusy] = useState(false);
  const [emailGrantMsg, setEmailGrantMsg] = useState(null); // { type: 'success'|'error', text }

  // Full-feature grant options (apply to BOTH search-grant and email-grant)
  const PAYMENT_METHODS = [
    { value: 'cash', label: 'Cash' },
    { value: 'stripe', label: 'Stripe' },
    { value: 'paypal', label: 'PayPal' },
    { value: 'bank_transfer', label: 'Bank Transfer' },
    { value: 'whatsapp', label: 'WhatsApp' },
    { value: 'admin_granted', label: 'Free / Admin Granted' },
    { value: 'other', label: 'Other' },
  ];
  const defaultPrice = typeof book.price === 'number' ? book.price : parseFloat(book.price || 0);
  const [grantPrice, setGrantPrice] = useState(defaultPrice);
  const [grantMethod, setGrantMethod] = useState('cash');
  const [grantNote, setGrantNote] = useState('');

  const fetchAllUsers = async () => {
    setUsersLoading(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/users`, { credentials: 'include' });
      if (r.ok) {
        const data = await r.json();
        const list = Array.isArray(data) ? data : (data.users || []);
        setUsersList(list);
      }
    } catch {}
    setUsersLoading(false);
  };

  const fetchAccess = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/books/admin/book-access/${book.book_id}`, { credentials: 'include' });
      const data = await res.json();
      setUsersWithAccess(data.users || []);
    } catch {}
    setLoading(false);
  };

  useEffect(() => {
    fetchAccess();
    // Always refetch users to make sure the list is fresh and not stale from parent
    fetchAllUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const grantAccess = async (userId) => {
    setGranting(userId);
    try {
      const res = await fetch(`${BACKEND_URL}/api/books/admin/grant-access`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({
          user_id: userId,
          book_id: book.book_id,
          amount: parseFloat(grantPrice) || 0,
          payment_method: grantMethod,
          note: grantNote || undefined,
        })
      });
      const data = await res.json();
      alert(data.message || data.detail || 'Done');
      fetchAccess();
    } catch { alert('Failed to grant access'); }
    setGranting(null);
  };

  const grantAccessByEmail = async () => {
    const email = emailInput.trim().toLowerCase();
    if (!email) { setEmailGrantMsg({ type: 'error', text: 'Enter an email first' }); return; }
    setEmailGrantBusy(true);
    setEmailGrantMsg(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/books/admin/grant-access`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({
          email,
          book_id: book.book_id,
          amount: parseFloat(grantPrice) || 0,
          payment_method: grantMethod,
          note: grantNote || undefined,
        })
      });
      const data = await res.json();
      if (res.ok) {
        setEmailGrantMsg({ type: 'success', text: data.message || 'Access granted.' });
        setEmailInput('');
        fetchAccess();
        fetchAllUsers();
      } else {
        setEmailGrantMsg({ type: 'error', text: data.detail || 'Failed to grant access' });
      }
    } catch {
      setEmailGrantMsg({ type: 'error', text: 'Network error' });
    }
    setEmailGrantBusy(false);
  };

  const revokeAccess = async (userId, userName) => {
    if (!window.confirm(`Revoke access for ${userName}?`)) return;
    try {
      await fetch(`${BACKEND_URL}/api/books/admin/revoke-access`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ user_id: userId, book_id: book.book_id })
      });
      fetchAccess();
    } catch { alert('Failed to revoke access'); }
  };

  const accessUserIds = usersWithAccess.map(u => u.user_id);
  const usersWithout = usersList.filter(u =>
    !accessUserIds.includes(u.user_id) &&
    (u.name?.toLowerCase().includes(searchTerm.toLowerCase()) || u.email?.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-gray-800 rounded-xl border border-gray-700 w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="text-xl font-bold text-white">Manage Access</h2>
            <p className="text-gray-400 text-sm">{book.title}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white text-2xl">&times;</button>
        </div>

        {/* Users with access */}
        <div className="mb-6">
          <h3 className="text-white font-semibold text-sm mb-3">Users with Access ({usersWithAccess.length})</h3>
          {loading ? (
            <p className="text-gray-500 text-sm">Loading...</p>
          ) : usersWithAccess.length === 0 ? (
            <p className="text-gray-500 text-sm bg-gray-900/50 rounded-lg p-4 text-center">No users have access yet</p>
          ) : (
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {usersWithAccess.map(u => (
                <div key={u.user_id} className="flex items-center justify-between bg-gray-900/50 rounded-lg px-4 py-2">
                  <div className="min-w-0 flex-1">
                    <p className="text-white text-sm font-medium truncate">{u.name}</p>
                    <p className="text-gray-500 text-xs truncate">{u.email} &middot; {u.payment_method === 'admin_granted' ? 'Manual' : u.payment_method || 'Stripe'}</p>
                  </div>
                  <Button size="sm" variant="outline" className="border-red-600/50 text-red-400 hover:bg-red-600/20 shrink-0 ml-2 h-7 text-xs" onClick={() => revokeAccess(u.user_id, u.name)}>
                    Revoke
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Grant Settings — applied to BOTH methods below */}
        <div className="mb-4 bg-gray-900/40 rounded-lg p-4 border border-gray-700 space-y-3">
          <p className="text-white font-semibold text-sm">Grant Settings <span className="text-gray-500 font-normal text-xs">(applied to both methods below)</span></p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-gray-400 text-xs mb-1">Price (USD)</label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={grantPrice}
                onChange={e => setGrantPrice(e.target.value)}
                className="bg-gray-700 border-gray-600 text-white text-sm"
                data-testid="grant-price-input"
              />
              <p className="text-gray-500 text-[10px] mt-0.5">Default: book's official price (${defaultPrice})</p>
            </div>
            <div>
              <label className="block text-gray-400 text-xs mb-1">Payment Method</label>
              <select
                value={grantMethod}
                onChange={e => setGrantMethod(e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 text-white text-sm rounded-md px-3 py-2 focus:outline-none focus:border-pink-500"
                data-testid="grant-method-select"
              >
                {PAYMENT_METHODS.map(pm => (
                  <option key={pm.value} value={pm.value}>{pm.label}</option>
                ))}
              </select>
              <p className="text-gray-500 text-[10px] mt-0.5">Default: Cash</p>
            </div>
          </div>
          <div>
            <label className="block text-gray-400 text-xs mb-1">Note <span className="text-gray-600">(optional)</span></label>
            <Input
              type="text"
              placeholder="e.g. WhatsApp transfer ref #12345"
              value={grantNote}
              onChange={e => setGrantNote(e.target.value)}
              className="bg-gray-700 border-gray-600 text-white text-sm"
              data-testid="grant-note-input"
            />
          </div>
        </div>

        {/* Grant access by email — fastest path */}
        <div className="mb-6 bg-gray-900/50 rounded-lg p-4 border border-pink-500/20">
          <h3 className="text-white font-semibold text-sm mb-2 flex items-center gap-2">
            <Mail className="w-4 h-4 text-pink-400" />
            Grant by Email
          </h3>
          <p className="text-gray-500 text-xs mb-3">Fastest way — paste the user's email and grant access instantly. They must have signed in at least once.</p>
          <div className="flex gap-2">
            <Input
              type="email"
              placeholder="user@example.com"
              value={emailInput}
              onChange={e => setEmailInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); grantAccessByEmail(); } }}
              className="bg-gray-700 border-gray-600 text-white flex-1"
              data-testid="grant-by-email-input"
            />
            <Button
              onClick={grantAccessByEmail}
              disabled={emailGrantBusy || !emailInput.trim()}
              className="bg-pink-600 hover:bg-pink-700 text-white whitespace-nowrap"
              data-testid="grant-by-email-btn"
            >
              {emailGrantBusy ? '...' : 'Grant'}
            </Button>
          </div>
          {emailGrantMsg && (
            <p className={`text-xs mt-2 ${emailGrantMsg.type === 'success' ? 'text-green-400' : 'text-red-400'}`}>
              {emailGrantMsg.text}
            </p>
          )}
        </div>

        {/* Grant access via search */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-white font-semibold text-sm">Or Search Registered Users</h3>
            <button
              onClick={fetchAllUsers}
              disabled={usersLoading}
              className="text-xs text-pink-400 hover:text-pink-300 disabled:opacity-50"
              data-testid="refresh-users-btn"
            >
              {usersLoading ? 'Loading…' : `↻ Refresh (${usersList.length})`}
            </button>
          </div>
          <Input
            placeholder="Search users by name or email..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="bg-gray-700 border-gray-600 text-white mb-3"
            data-testid="access-search-input"
          />
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {usersLoading ? (
              <p className="text-gray-500 text-sm text-center py-3">Loading users…</p>
            ) : usersWithout.length === 0 ? (
              <p className="text-gray-500 text-sm text-center py-3">
                {searchTerm
                  ? 'No matching users'
                  : usersList.length === 0
                    ? 'No registered users yet — use "Grant by Email" above'
                    : 'All registered users already have access'}
              </p>
            ) : (
              usersWithout.slice(0, 20).map(u => (
                <div key={u.user_id} className="flex items-center justify-between bg-gray-900/50 rounded-lg px-4 py-2">
                  <div className="min-w-0 flex-1">
                    <p className="text-white text-sm font-medium truncate">{u.name}</p>
                    <p className="text-gray-500 text-xs truncate">{u.email}</p>
                  </div>
                  <Button
                    size="sm"
                    disabled={granting === u.user_id}
                    className="bg-green-600 hover:bg-green-700 text-white shrink-0 ml-2 h-7 text-xs"
                    onClick={() => grantAccess(u.user_id)}
                    data-testid={`grant-access-${u.user_id}`}
                  >
                    {granting === u.user_id ? '...' : 'Grant Access'}
                  </Button>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Edit Book Modal
const EditBookModal = ({ book, onClose, onSave }) => {
  const [form, setForm] = useState({
    title: book.title || '',
    subtitle: book.subtitle || '',
    author: book.author || '',
    description: book.description || '',
    price: book.price || 0,
    is_free: book.is_free ?? true,
    is_downloadable: book.is_downloadable ?? false,
    payment_link: book.payment_link || '',
    status: book.status || 'active'
  });
  const [coverFile, setCoverFile] = useState(null);
  const [pdfFile, setPdfFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // PDF upload progress
  const [progress, setProgress] = useState({ percent: 0, loaded: 0, total: 0, speed: 0, eta: 0, stage: '' });
  const xhrRef = React.useRef(null);
  const startRef = React.useRef(0);

  const MAX_PDF_BYTES = 200 * 1024 * 1024;
  const MAX_COVER_BYTES = 20 * 1024 * 1024;

  const fmtBytes = (b) => {
    if (!b) return '0 B';
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    if (b < 1024 * 1024 * 1024) return `${(b / (1024 * 1024)).toFixed(2)} MB`;
    return `${(b / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  };
  const fmtSpeed = (s) => `${fmtBytes(s)}/s`;
  const fmtEta = (sec) => {
    if (!sec || !isFinite(sec)) return '—';
    if (sec < 60) return `${Math.ceil(sec)}s`;
    return `${Math.floor(sec / 60)}m ${Math.ceil(sec % 60)}s`;
  };

  const handlePdfSelect = (file) => {
    if (file && file.size > MAX_PDF_BYTES) {
      setError(`PDF too large (${(file.size / (1024 * 1024)).toFixed(1)} MB). Maximum allowed is 200 MB.`);
      setPdfFile(null);
      return;
    }
    setError('');
    setPdfFile(file);
  };

  const handleCoverSelect = (file) => {
    if (file && file.size > MAX_COVER_BYTES) {
      setError(`Cover too large (${(file.size / (1024 * 1024)).toFixed(1)} MB). Maximum allowed is 20 MB.`);
      setCoverFile(null);
      return;
    }
    setError('');
    setCoverFile(file);
  };

  const handleCancelUpload = () => {
    if (xhrRef.current) { xhrRef.current.abort(); xhrRef.current = null; }
    setSaving(false);
    setProgress({ percent: 0, loaded: 0, total: 0, speed: 0, eta: 0, stage: '' });
    setError('Upload cancelled');
  };

  const handleSave = () => {
    setSaving(true);
    setError('');
    setProgress({ percent: 0, loaded: 0, total: 0, speed: 0, eta: 0, stage: pdfFile ? 'uploading' : 'processing' });
    startRef.current = Date.now();

    const formPayload = new FormData();
    Object.entries(form).forEach(([key, val]) => {
      if (val !== null && val !== undefined) formPayload.append(key, val);
    });
    if (coverFile) formPayload.append('cover_file', coverFile);
    if (pdfFile) formPayload.append('pdf_file', pdfFile);

    const xhr = new XMLHttpRequest();
    xhrRef.current = xhr;
    xhr.open('PUT', `${BACKEND_URL}/api/books/${book.book_id}`);
    xhr.withCredentials = true;

    xhr.upload.onprogress = (ev) => {
      if (!ev.lengthComputable) return;
      const elapsed = Math.max((Date.now() - startRef.current) / 1000, 0.001);
      const speed = ev.loaded / elapsed;
      const remaining = ev.total - ev.loaded;
      setProgress({
        percent: Math.round((ev.loaded / ev.total) * 100),
        loaded: ev.loaded,
        total: ev.total,
        speed,
        eta: speed > 0 ? remaining / speed : 0,
        stage: ev.loaded < ev.total ? 'uploading' : 'processing',
      });
    };

    xhr.upload.onload = () => setProgress((p) => ({ ...p, percent: 100, stage: 'processing' }));

    xhr.onreadystatechange = () => {
      if (xhr.readyState !== 4) return;
      xhrRef.current = null;
      if (xhr.status >= 200 && xhr.status < 300) {
        setProgress((p) => ({ ...p, percent: 100, stage: 'done' }));
        setTimeout(() => {
          setSaving(false);
          onSave(); // parent just refetches; data already saved server-side
        }, 600);
      } else {
        let detail = 'Update failed';
        try { detail = JSON.parse(xhr.responseText).detail || detail; } catch (_) {}
        setError(detail);
        setSaving(false);
        setProgress({ percent: 0, loaded: 0, total: 0, speed: 0, eta: 0, stage: '' });
      }
    };

    xhr.onerror = () => {
      xhrRef.current = null;
      setError('Connection error during update');
      setSaving(false);
      setProgress({ percent: 0, loaded: 0, total: 0, speed: 0, eta: 0, stage: '' });
    };

    xhr.send(formPayload);
  };

  const f = (key, val) => setForm(p => ({ ...p, [key]: val }));

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-gray-800 rounded-xl border border-gray-700 w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-white">Edit Book</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white text-2xl">&times;</button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-gray-300 mb-1 text-sm">Title</label>
            <Input value={form.title} onChange={e => f('title', e.target.value)} className="bg-gray-700 border-gray-600 text-white" data-testid="edit-book-title" />
          </div>

          <div>
            <label className="block text-gray-300 mb-1 text-sm">Subtitle</label>
            <Input value={form.subtitle} onChange={e => f('subtitle', e.target.value)} className="bg-gray-700 border-gray-600 text-white" data-testid="edit-book-subtitle" />
          </div>

          <div>
            <label className="block text-gray-300 mb-1 text-sm">Author</label>
            <Input value={form.author} onChange={e => f('author', e.target.value)} className="bg-gray-700 border-gray-600 text-white" data-testid="edit-book-author" />
          </div>

          <div>
            <label className="block text-gray-300 mb-1 text-sm">Description</label>
            <textarea value={form.description} onChange={e => f('description', e.target.value)} rows={3} className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg p-3 text-sm" data-testid="edit-book-description" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-gray-300 mb-1 text-sm">Price ($)</label>
              <Input type="number" step="0.01" min="0" value={form.price} onChange={e => f('price', parseFloat(e.target.value) || 0)} className="bg-gray-700 border-gray-600 text-white" data-testid="edit-book-price" />
            </div>
            <div>
              <label className="block text-gray-300 mb-1 text-sm">Status</label>
              <select value={form.status} onChange={e => f('status', e.target.value)} className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg p-2 text-sm" data-testid="edit-book-status">
                <option value="active">Active</option>
                <option value="draft">Draft</option>
                <option value="archived">Archived</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-gray-300 mb-1 text-sm">Payment Link (URL)</label>
            <Input type="url" placeholder="https://..." value={form.payment_link} onChange={e => f('payment_link', e.target.value)} className="bg-gray-700 border-gray-600 text-white" data-testid="edit-book-payment-link" />
          </div>

          <div>
            <label className="block text-gray-300 mb-1 text-sm">Replace Cover Image <span className="text-gray-500 text-xs">(max 20 MB)</span></label>
            <input type="file" accept="image/*" onChange={e => handleCoverSelect(e.target.files[0])} className="w-full text-gray-400 text-sm" data-testid="edit-book-cover" />
            {coverFile && <p className="text-pink-400 text-xs mt-1">{fmtBytes(coverFile.size)}</p>}
          </div>

          {/* Replace PDF — highlighted as this is the advanced new feature */}
          <div className="bg-gray-900/50 rounded-lg p-4 border border-pink-500/20">
            <label className="block text-white mb-2 text-sm font-semibold flex items-center gap-2">
              <FileText className="w-4 h-4 text-pink-400" />
              Replace PDF Content
              <span className="text-gray-500 text-xs font-normal ml-auto">(max 200 MB)</span>
            </label>
            <p className="text-gray-500 text-xs mb-3">Upload a new version of the book. Existing readers' bookmarks and progress are preserved. The new PDF replaces the old one in object storage immediately.</p>
            <input
              type="file"
              accept=".pdf,application/pdf"
              onChange={e => handlePdfSelect(e.target.files[0])}
              className="w-full text-gray-400 text-sm"
              data-testid="edit-book-pdf"
            />
            {pdfFile && (
              <div className="mt-2 flex items-center gap-2 text-xs">
                <span className="bg-pink-500/20 text-pink-300 px-2 py-1 rounded">NEW PDF</span>
                <span className="text-gray-300 truncate flex-1">{pdfFile.name}</span>
                <span className="text-pink-400">{fmtBytes(pdfFile.size)}</span>
                <button type="button" onClick={() => setPdfFile(null)} className="text-gray-500 hover:text-red-400" title="Remove">&times;</button>
              </div>
            )}
          </div>

          <div className="flex gap-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <button type="button" onClick={() => f('is_free', !form.is_free)} className={`w-10 h-5 rounded-full transition-colors ${form.is_free ? 'bg-green-500' : 'bg-gray-600'}`}>
                <div className={`w-4 h-4 rounded-full bg-white transition-transform ${form.is_free ? 'translate-x-5' : 'translate-x-0.5'}`} />
              </button>
              <span className="text-gray-300 text-sm">Free</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <button type="button" onClick={() => f('is_downloadable', !form.is_downloadable)} className={`w-10 h-5 rounded-full transition-colors ${form.is_downloadable ? 'bg-green-500' : 'bg-gray-600'}`}>
                <div className={`w-4 h-4 rounded-full bg-white transition-transform ${form.is_downloadable ? 'translate-x-5' : 'translate-x-0.5'}`} />
              </button>
              <span className="text-gray-300 text-sm">Downloadable</span>
            </label>
          </div>

          {error && (
            <div className="bg-red-500/20 border border-red-500/50 text-red-400 px-4 py-2 rounded-lg text-sm" data-testid="edit-book-error">
              {error}
            </div>
          )}

          {/* Advanced PDF replace progress panel */}
          {saving && pdfFile && (
            <div className="bg-gray-900/80 border border-pink-500/30 rounded-xl p-5 space-y-4" data-testid="edit-progress-panel">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="relative w-10 h-10">
                    <div className="absolute inset-0 rounded-full border-2 border-pink-500/30"></div>
                    <div className={`absolute inset-0 rounded-full border-2 border-pink-500 border-t-transparent ${progress.stage === 'done' ? '' : 'animate-spin'}`}></div>
                    <div className="absolute inset-0 flex items-center justify-center text-pink-400 text-xs font-semibold">
                      {progress.stage === 'done' ? '✓' : `${progress.percent}%`}
                    </div>
                  </div>
                  <div>
                    <p className="text-white font-medium text-sm">
                      {progress.stage === 'uploading' && 'Uploading new PDF…'}
                      {progress.stage === 'processing' && 'Saving to object storage…'}
                      {progress.stage === 'done' && 'PDF replaced successfully!'}
                    </p>
                    <p className="text-gray-400 text-xs">
                      {progress.stage === 'uploading'
                        ? `${fmtBytes(progress.loaded)} of ${fmtBytes(progress.total)}`
                        : progress.stage === 'processing'
                          ? 'Finalizing and updating book record…'
                          : 'Existing readers will see the new content on next read.'}
                    </p>
                  </div>
                </div>
                {(progress.stage === 'uploading' || progress.stage === 'processing') && (
                  <button type="button" onClick={handleCancelUpload} className="text-xs text-gray-400 hover:text-red-400 border border-gray-700 rounded px-3 py-1" data-testid="edit-cancel-btn">
                    Cancel
                  </button>
                )}
              </div>
              <div className="relative w-full h-3 bg-gray-800 rounded-full overflow-hidden">
                <div
                  className="absolute inset-y-0 left-0 bg-gradient-to-r from-pink-500 via-purple-500 to-pink-500 bg-[length:200%_100%] animate-[shimmer_2s_linear_infinite] transition-[width] duration-300"
                  style={{ width: `${progress.percent}%` }}
                />
              </div>
              <div className="grid grid-cols-3 gap-3 text-center text-xs">
                <div className="bg-gray-800/60 rounded-lg p-2">
                  <p className="text-gray-500 uppercase tracking-wide">Speed</p>
                  <p className="text-pink-300 font-semibold mt-0.5">{progress.stage === 'uploading' ? fmtSpeed(progress.speed) : '—'}</p>
                </div>
                <div className="bg-gray-800/60 rounded-lg p-2">
                  <p className="text-gray-500 uppercase tracking-wide">ETA</p>
                  <p className="text-pink-300 font-semibold mt-0.5">{progress.stage === 'uploading' ? fmtEta(progress.eta) : progress.stage === 'processing' ? 'finishing…' : 'done'}</p>
                </div>
                <div className="bg-gray-800/60 rounded-lg p-2">
                  <p className="text-gray-500 uppercase tracking-wide">Stage</p>
                  <p className="text-pink-300 font-semibold mt-0.5 capitalize">{progress.stage || '—'}</p>
                </div>
              </div>
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <Button onClick={onClose} disabled={saving} variant="outline" className="flex-1 border-gray-600 text-gray-300">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="flex-1 bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-700 hover:to-purple-700 text-white" data-testid="edit-book-save-btn">
              {saving
                ? (pdfFile
                    ? (progress.stage === 'processing' ? 'Processing…' : `Uploading ${progress.percent}%`)
                    : 'Saving…')
                : (pdfFile ? 'Save Changes & Replace PDF' : 'Save Changes')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Book Upload Form
const BookUploadForm = ({ onSuccess }) => {
  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    author: '',
    description: '',
    price: 0,
    is_free: true,
    is_downloadable: false,
    status: 'active',
    payment_link: ''
  });
  const [pdfFile, setPdfFile] = useState(null);
  const [coverFile, setCoverFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  // Advanced upload progress state
  const [progress, setProgress] = useState({
    percent: 0,
    loaded: 0,
    total: 0,
    speed: 0,         // bytes per second
    eta: 0,           // seconds remaining
    stage: '',        // 'uploading' | 'processing' | 'done'
  });
  const xhrRef = React.useRef(null);
  const startRef = React.useRef(0);

  const MAX_PDF_BYTES = 200 * 1024 * 1024;   // 200 MB
  const MAX_COVER_BYTES = 20 * 1024 * 1024;  // 20 MB

  const handlePdfSelect = (file) => {
    if (file && file.size > MAX_PDF_BYTES) {
      setError(`PDF too large (${(file.size / (1024 * 1024)).toFixed(1)} MB). Maximum allowed is 200 MB.`);
      setPdfFile(null);
      return;
    }
    setError('');
    setPdfFile(file);
  };

  const handleCoverSelect = (file) => {
    if (file && file.size > MAX_COVER_BYTES) {
      setError(`Cover too large (${(file.size / (1024 * 1024)).toFixed(1)} MB). Maximum allowed is 20 MB.`);
      setCoverFile(null);
      return;
    }
    setError('');
    setCoverFile(file);
  };

  const formatBytes = (b) => {
    if (!b) return '0 B';
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    if (b < 1024 * 1024 * 1024) return `${(b / (1024 * 1024)).toFixed(2)} MB`;
    return `${(b / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  };
  const formatSpeed = (s) => `${formatBytes(s)}/s`;
  const formatEta = (sec) => {
    if (!sec || !isFinite(sec)) return '—';
    if (sec < 60) return `${Math.ceil(sec)}s`;
    const m = Math.floor(sec / 60);
    const r = Math.ceil(sec % 60);
    return `${m}m ${r}s`;
  };

  const handleCancel = () => {
    if (xhrRef.current) {
      xhrRef.current.abort();
      xhrRef.current = null;
    }
    setUploading(false);
    setProgress({ percent: 0, loaded: 0, total: 0, speed: 0, eta: 0, stage: '' });
    setError('Upload cancelled');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!pdfFile) {
      setError('Please select a PDF file');
      return;
    }

    setUploading(true);
    setError('');
    setProgress({ percent: 0, loaded: 0, total: 0, speed: 0, eta: 0, stage: 'uploading' });
    startRef.current = Date.now();

    const formPayload = new FormData();
    formPayload.append('pdf_file', pdfFile);
    if (coverFile) formPayload.append('cover_file', coverFile);
    formPayload.append('title', formData.title);
    formPayload.append('subtitle', formData.subtitle);
    formPayload.append('author', formData.author);
    formPayload.append('description', formData.description);
    formPayload.append('price', formData.price);
    formPayload.append('is_free', formData.is_free);
    formPayload.append('is_downloadable', formData.is_downloadable);
    formPayload.append('payment_link', formData.payment_link || '');
    formPayload.append('status', formData.status);

    const xhr = new XMLHttpRequest();
    xhrRef.current = xhr;
    xhr.open('POST', `${BACKEND_URL}/api/books/upload`);
    xhr.withCredentials = true;

    xhr.upload.onprogress = (ev) => {
      if (!ev.lengthComputable) return;
      const elapsed = Math.max((Date.now() - startRef.current) / 1000, 0.001);
      const speed = ev.loaded / elapsed;
      const remaining = ev.total - ev.loaded;
      const eta = speed > 0 ? remaining / speed : 0;
      setProgress({
        percent: Math.round((ev.loaded / ev.total) * 100),
        loaded: ev.loaded,
        total: ev.total,
        speed,
        eta,
        stage: ev.loaded < ev.total ? 'uploading' : 'processing',
      });
    };

    xhr.upload.onload = () => {
      // Upload to server finished — backend now processes/uploads to object storage
      setProgress((p) => ({ ...p, percent: 100, stage: 'processing' }));
    };

    xhr.onreadystatechange = () => {
      if (xhr.readyState !== 4) return;
      xhrRef.current = null;
      if (xhr.status >= 200 && xhr.status < 300) {
        setProgress((p) => ({ ...p, percent: 100, stage: 'done' }));
        // Short delay so the user sees the "Done" state
        setTimeout(() => {
          setUploading(false);
          onSuccess();
        }, 700);
      } else {
        let detail = 'Upload failed';
        try {
          const json = JSON.parse(xhr.responseText);
          detail = json.detail || detail;
        } catch (_) { /* noop */ }
        setError(detail);
        setUploading(false);
        setProgress({ percent: 0, loaded: 0, total: 0, speed: 0, eta: 0, stage: '' });
      }
    };

    xhr.onerror = () => {
      xhrRef.current = null;
      setError('Connection error during upload');
      setUploading(false);
      setProgress({ percent: 0, loaded: 0, total: 0, speed: 0, eta: 0, stage: '' });
    };

    xhr.send(formPayload);
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-white mb-6">Upload New Book</h2>
      
      <form onSubmit={handleSubmit} className="max-w-2xl">
        <div className="bg-gray-800 rounded-xl p-6 border border-gray-700 space-y-6">
          {/* PDF File */}
          <div>
            <label className="block text-gray-300 mb-2">PDF File <span className="text-gray-500 text-xs">(max 200 MB)</span> *</label>
            <div className="border-2 border-dashed border-gray-600 rounded-lg p-6 text-center hover:border-pink-500 transition-colors">
              <input
                type="file"
                accept=".pdf,application/pdf"
                onChange={(e) => handlePdfSelect(e.target.files[0])}
                className="hidden"
                id="pdf-upload"
              />
              <label htmlFor="pdf-upload" className="cursor-pointer">
                <FileText className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                <p className="text-gray-400">
                  {pdfFile ? pdfFile.name : 'Click to select PDF file'}
                </p>
                {pdfFile && (
                  <p className="text-pink-400 text-xs mt-1">{formatBytes(pdfFile.size)}</p>
                )}
              </label>
            </div>
          </div>

          {/* Cover Image */}
          <div>
            <label className="block text-gray-300 mb-2">Cover Image <span className="text-gray-500 text-xs">(max 20 MB)</span></label>
            <div className="border-2 border-dashed border-gray-600 rounded-lg p-6 text-center hover:border-pink-500 transition-colors">
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleCoverSelect(e.target.files[0])}
                className="hidden"
                id="cover-upload"
              />
              <label htmlFor="cover-upload" className="cursor-pointer">
                <Image className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                <p className="text-gray-400">
                  {coverFile ? coverFile.name : 'Click to select cover image'}
                </p>
                {coverFile && (
                  <p className="text-pink-400 text-xs mt-1">{formatBytes(coverFile.size)}</p>
                )}
              </label>
            </div>
          </div>

          {/* Title */}
          <div>
            <label className="block text-gray-300 mb-2">Title *</label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
              className="bg-gray-700 border-gray-600 text-white"
            />
          </div>

          {/* Subtitle */}
          <div>
            <label className="block text-gray-300 mb-2">Subtitle</label>
            <Input
              value={formData.subtitle}
              onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
              className="bg-gray-700 border-gray-600 text-white"
            />
          </div>

          {/* Author */}
          <div>
            <label className="block text-gray-300 mb-2">Author *</label>
            <Input
              value={formData.author}
              onChange={(e) => setFormData({ ...formData, author: e.target.value })}
              required
              className="bg-gray-700 border-gray-600 text-white"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-gray-300 mb-2">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={4}
              className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg p-3"
            />
          </div>

          {/* Price and Options */}
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="block text-gray-300 mb-2">Price ($)</label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) })}
                className="bg-gray-700 border-gray-600 text-white"
              />
            </div>
            <div>
              <label className="block text-gray-300 mb-2">Status</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg p-2"
              >
                <option value="active">Active</option>
                <option value="draft">Draft</option>
                <option value="archived">Archived</option>
              </select>
            </div>
          </div>

          {/* Toggles */}
          <div>
            <label className="block text-gray-300 mb-2">Payment Link (URL)</label>
            <Input
              type="url"
              placeholder="https://payment-link.example.com/..."
              value={formData.payment_link}
              onChange={(e) => setFormData({ ...formData, payment_link: e.target.value })}
              className="bg-gray-700 border-gray-600 text-white"
              data-testid="book-payment-link-input"
            />
            <p className="text-gray-500 text-xs mt-1">External payment link for this book (Stripe, etc.)</p>
          </div>

          {/* Toggles */}
          <div className="flex gap-8">
            <label className="flex items-center gap-3 cursor-pointer">
              <button
                type="button"
                onClick={() => setFormData({ ...formData, is_free: !formData.is_free })}
                className={`w-12 h-6 rounded-full transition-colors ${
                  formData.is_free ? 'bg-green-500' : 'bg-gray-600'
                }`}
              >
                <div className={`w-5 h-5 rounded-full bg-white transition-transform ${
                  formData.is_free ? 'translate-x-6' : 'translate-x-1'
                }`} />
              </button>
              <span className="text-gray-300">Free Book</span>
            </label>

            <label className="flex items-center gap-3 cursor-pointer">
              <button
                type="button"
                onClick={() => setFormData({ ...formData, is_downloadable: !formData.is_downloadable })}
                className={`w-12 h-6 rounded-full transition-colors ${
                  formData.is_downloadable ? 'bg-green-500' : 'bg-gray-600'
                }`}
              >
                <div className={`w-5 h-5 rounded-full bg-white transition-transform ${
                  formData.is_downloadable ? 'translate-x-6' : 'translate-x-1'
                }`} />
              </button>
              <span className="text-gray-300">Downloadable</span>
            </label>
          </div>

          {error && (
            <div className="bg-red-500/20 border border-red-500/50 text-red-400 px-4 py-2 rounded-lg text-sm" data-testid="upload-error">
              {error}
            </div>
          )}

          {/* Advanced upload progress panel */}
          {uploading && (
            <div className="bg-gray-900/80 border border-pink-500/30 rounded-xl p-5 space-y-4" data-testid="upload-progress-panel">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="relative w-10 h-10">
                    <div className="absolute inset-0 rounded-full border-2 border-pink-500/30"></div>
                    <div className={`absolute inset-0 rounded-full border-2 border-pink-500 border-t-transparent ${progress.stage === 'done' ? '' : 'animate-spin'}`}></div>
                    <div className="absolute inset-0 flex items-center justify-center text-pink-400 text-xs font-semibold">
                      {progress.stage === 'done' ? '✓' : `${progress.percent}%`}
                    </div>
                  </div>
                  <div>
                    <p className="text-white font-medium text-sm">
                      {progress.stage === 'uploading' && 'Uploading file…'}
                      {progress.stage === 'processing' && 'Processing on server…'}
                      {progress.stage === 'done' && 'Upload complete!'}
                    </p>
                    <p className="text-gray-400 text-xs">
                      {progress.stage === 'uploading'
                        ? `${formatBytes(progress.loaded)} of ${formatBytes(progress.total)}`
                        : progress.stage === 'processing'
                          ? 'Saving to object storage and finalizing…'
                          : 'Book is now in your library.'}
                    </p>
                  </div>
                </div>
                {(progress.stage === 'uploading' || progress.stage === 'processing') && (
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="text-xs text-gray-400 hover:text-red-400 transition-colors border border-gray-700 rounded px-3 py-1"
                    data-testid="upload-cancel-btn"
                  >
                    Cancel
                  </button>
                )}
              </div>

              {/* Progress bar */}
              <div className="relative w-full h-3 bg-gray-800 rounded-full overflow-hidden">
                <div
                  className="absolute inset-y-0 left-0 bg-gradient-to-r from-pink-500 via-purple-500 to-pink-500 bg-[length:200%_100%] animate-[shimmer_2s_linear_infinite] transition-[width] duration-300"
                  style={{ width: `${progress.percent}%` }}
                />
              </div>

              {/* Stats row */}
              <div className="grid grid-cols-3 gap-3 text-center text-xs">
                <div className="bg-gray-800/60 rounded-lg p-2">
                  <p className="text-gray-500 uppercase tracking-wide">Speed</p>
                  <p className="text-pink-300 font-semibold mt-0.5">
                    {progress.stage === 'uploading' ? formatSpeed(progress.speed) : '—'}
                  </p>
                </div>
                <div className="bg-gray-800/60 rounded-lg p-2">
                  <p className="text-gray-500 uppercase tracking-wide">ETA</p>
                  <p className="text-pink-300 font-semibold mt-0.5">
                    {progress.stage === 'uploading' ? formatEta(progress.eta) : progress.stage === 'processing' ? 'finishing…' : 'done'}
                  </p>
                </div>
                <div className="bg-gray-800/60 rounded-lg p-2">
                  <p className="text-gray-500 uppercase tracking-wide">Stage</p>
                  <p className="text-pink-300 font-semibold mt-0.5 capitalize">{progress.stage || '—'}</p>
                </div>
              </div>

              {/* Step indicator */}
              <div className="flex items-center justify-between text-[10px] uppercase tracking-wider">
                <div className={`flex items-center gap-1 ${progress.stage === 'uploading' || progress.stage === 'processing' || progress.stage === 'done' ? 'text-pink-400' : 'text-gray-600'}`}>
                  <span className="w-2 h-2 rounded-full bg-current"></span> Upload
                </div>
                <div className={`flex-1 h-px mx-2 ${progress.stage === 'processing' || progress.stage === 'done' ? 'bg-pink-400' : 'bg-gray-700'}`}></div>
                <div className={`flex items-center gap-1 ${progress.stage === 'processing' || progress.stage === 'done' ? 'text-pink-400' : 'text-gray-600'}`}>
                  <span className="w-2 h-2 rounded-full bg-current"></span> Process
                </div>
                <div className={`flex-1 h-px mx-2 ${progress.stage === 'done' ? 'bg-pink-400' : 'bg-gray-700'}`}></div>
                <div className={`flex items-center gap-1 ${progress.stage === 'done' ? 'text-green-400' : 'text-gray-600'}`}>
                  <span className="w-2 h-2 rounded-full bg-current"></span> Done
                </div>
              </div>
            </div>
          )}

          <Button
            type="submit"
            disabled={uploading || !formData.title || !formData.author || !pdfFile}
            className="w-full bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-700 hover:to-purple-700 text-white py-6 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="upload-submit-btn"
          >
            {uploading
              ? `${progress.stage === 'processing' ? 'Processing…' : `Uploading… ${progress.percent}%`}`
              : 'Upload Book'}
          </Button>
        </div>
      </form>
    </div>
  );
};

// ==================== Purchases Tab ====================
const PurchasesTab = () => {
  const [data, setData] = useState({ purchases: [], totals: { count: 0, revenue: 0, by_method: {} } });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [methodFilter, setMethodFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('created_at_desc');
  const [selected, setSelected] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/purchases`, { credentials: 'include' });
      if (r.ok) setData(await r.json());
    } catch {}
    setLoading(false);
  };
  useEffect(() => { fetchData(); }, []);

  const allMethods = Array.from(new Set(data.purchases.map(p => p.payment_method).filter(Boolean)));
  const allStatuses = Array.from(new Set(data.purchases.map(p => p.status).filter(Boolean)));

  const filtered = data.purchases
    .filter(p => {
      if (methodFilter !== 'all' && p.payment_method !== methodFilter) return false;
      if (statusFilter !== 'all' && p.status !== statusFilter) return false;
      if (!search) return true;
      const s = search.toLowerCase();
      return (
        (p.user_name || '').toLowerCase().includes(s) ||
        (p.user_email || '').toLowerCase().includes(s) ||
        (p.book_title || '').toLowerCase().includes(s) ||
        (p.purchase_id || '').toLowerCase().includes(s) ||
        (p.session_id || '').toLowerCase().includes(s) ||
        (p.note || '').toLowerCase().includes(s)
      );
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'created_at_asc': return (a.created_at || '').localeCompare(b.created_at || '');
        case 'amount_desc': return (b.amount || 0) - (a.amount || 0);
        case 'amount_asc': return (a.amount || 0) - (b.amount || 0);
        case 'user_name': return (a.user_name || '').localeCompare(b.user_name || '');
        case 'book_title': return (a.book_title || '').localeCompare(b.book_title || '');
        default: return (b.created_at || '').localeCompare(a.created_at || '');
      }
    });

  const filteredRevenue = filtered.reduce((s, p) => s + (p.amount || 0), 0);

  const exportCsv = () => {
    const headers = ['Date','User Name','User Email','User ID','Book Title','Book ID','Amount (USD)','Payment Method','Status','Granted by Admin','Note','Session ID','Capture ID'];
    const rows = filtered.map(p => [
      p.created_at, p.user_name, p.user_email, p.user_id,
      p.book_title, p.book_id, p.amount?.toFixed(2),
      p.payment_method, p.status, p.granted_by_admin ? 'YES' : '',
      (p.note || '').replace(/"/g,'""'), p.session_id, p.paypal_capture_id,
    ].map(v => `"${v ?? ''}"`).join(','));
    const csv = headers.join(',') + '\n' + rows.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `purchases_${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const fmtDate = (s) => {
    if (!s) return '—';
    try { return new Date(s).toLocaleString(); } catch { return s; }
  };
  const methodColor = (m) => ({
    cash: 'bg-emerald-500/20 text-emerald-300',
    stripe: 'bg-indigo-500/20 text-indigo-300',
    paypal: 'bg-yellow-500/20 text-yellow-300',
    bank_transfer: 'bg-sky-500/20 text-sky-300',
    whatsapp: 'bg-green-500/20 text-green-300',
    admin_granted: 'bg-purple-500/20 text-purple-300',
    other: 'bg-gray-500/20 text-gray-300',
  }[m] || 'bg-gray-500/20 text-gray-300');

  return (
    <div data-testid="purchases-tab">
      <div className="flex flex-wrap justify-between items-start gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold text-white">Purchases</h2>
          <p className="text-gray-400 text-sm mt-1">Every single purchase across the platform — fully enriched.</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={fetchData} variant="outline" className="border-gray-600 text-gray-300" data-testid="purchases-refresh-btn">
            ↻ Refresh
          </Button>
          <Button onClick={exportCsv} className="bg-pink-600 hover:bg-pink-700 text-white" data-testid="purchases-export-btn">
            Export CSV
          </Button>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-gray-800/60 rounded-xl p-4 border border-gray-700">
          <p className="text-gray-400 text-xs uppercase tracking-wider">Total Purchases</p>
          <p className="text-3xl font-bold text-white mt-1" data-testid="kpi-total-count">{data.totals.count}</p>
        </div>
        <div className="bg-gray-800/60 rounded-xl p-4 border border-pink-500/30">
          <p className="text-gray-400 text-xs uppercase tracking-wider">Total Revenue</p>
          <p className="text-3xl font-bold text-pink-400 mt-1" data-testid="kpi-total-revenue">${data.totals.revenue.toFixed(2)}</p>
        </div>
        <div className="bg-gray-800/60 rounded-xl p-4 border border-gray-700">
          <p className="text-gray-400 text-xs uppercase tracking-wider">Filtered Rows</p>
          <p className="text-3xl font-bold text-white mt-1">{filtered.length}</p>
          <p className="text-xs text-gray-500 mt-0.5">${filteredRevenue.toFixed(2)} filtered revenue</p>
        </div>
        <div className="bg-gray-800/60 rounded-xl p-4 border border-gray-700">
          <p className="text-gray-400 text-xs uppercase tracking-wider mb-2">By Method</p>
          <div className="space-y-0.5">
            {Object.entries(data.totals.by_method).slice(0, 4).map(([k, v]) => (
              <div key={k} className="flex justify-between text-xs">
                <span className="text-gray-300 capitalize">{k.replace(/_/g, ' ')}</span>
                <span className="text-pink-400 font-semibold">{v}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-gray-800/40 rounded-xl p-4 mb-4 grid grid-cols-1 md:grid-cols-4 gap-3 border border-gray-700">
        <Input
          placeholder="Search user, book, note, ID..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="bg-gray-700 border-gray-600 text-white text-sm"
          data-testid="purchases-search-input"
        />
        <select value={methodFilter} onChange={e => setMethodFilter(e.target.value)} className="bg-gray-700 border border-gray-600 text-white text-sm rounded-md px-3 py-2 focus:outline-none focus:border-pink-500" data-testid="purchases-method-filter">
          <option value="all">All payment methods</option>
          {allMethods.map(m => <option key={m} value={m}>{m}</option>)}
        </select>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="bg-gray-700 border border-gray-600 text-white text-sm rounded-md px-3 py-2 focus:outline-none focus:border-pink-500" data-testid="purchases-status-filter">
          <option value="all">All statuses</option>
          {allStatuses.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={sortBy} onChange={e => setSortBy(e.target.value)} className="bg-gray-700 border border-gray-600 text-white text-sm rounded-md px-3 py-2 focus:outline-none focus:border-pink-500" data-testid="purchases-sort">
          <option value="created_at_desc">Newest first</option>
          <option value="created_at_asc">Oldest first</option>
          <option value="amount_desc">Highest amount</option>
          <option value="amount_asc">Lowest amount</option>
          <option value="user_name">User name (A→Z)</option>
          <option value="book_title">Book title (A→Z)</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-gray-800/40 rounded-xl border border-gray-700 overflow-hidden">
        {loading ? (
          <p className="text-gray-400 text-center py-12">Loading purchases…</p>
        ) : filtered.length === 0 ? (
          <p className="text-gray-500 text-center py-12">{data.purchases.length === 0 ? 'No purchases yet.' : 'No purchases match your filters.'}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-900/60 text-gray-400 uppercase text-xs">
                <tr>
                  <th className="px-4 py-3 text-left">Date</th>
                  <th className="px-4 py-3 text-left">User</th>
                  <th className="px-4 py-3 text-left">Book</th>
                  <th className="px-4 py-3 text-right">Amount</th>
                  <th className="px-4 py-3 text-left">Method</th>
                  <th className="px-4 py-3 text-left">Status</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {filtered.map((p, idx) => (
                  <tr key={p.session_id + idx} className="hover:bg-gray-700/30 transition-colors">
                    <td className="px-4 py-3 text-gray-300 whitespace-nowrap">{fmtDate(p.created_at)}</td>
                    <td className="px-4 py-3">
                      <p className="text-white font-medium">{p.user_name}</p>
                      <p className="text-gray-500 text-xs">{p.user_email}</p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-white truncate max-w-[260px]">{p.book_title}</p>
                      <p className="text-gray-500 text-xs">{p.book_author}</p>
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <span className="text-pink-400 font-semibold">${(p.amount || 0).toFixed(2)}</span>
                      {p.amount !== p.book_official_price && p.book_official_price > 0 && (
                        <p className="text-[10px] text-gray-500">list ${p.book_official_price.toFixed(2)}</p>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded text-xs capitalize ${methodColor(p.payment_method)}`}>
                        {(p.payment_method || 'unknown').replace(/_/g, ' ')}
                      </span>
                      {p.granted_by_admin && (
                        <span className="ml-1 px-1.5 py-0.5 rounded text-[9px] bg-purple-500/20 text-purple-300 uppercase">Manual</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-300 capitalize">{p.status}</td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => setSelected(p)} className="text-pink-400 hover:text-pink-300 text-xs underline" data-testid={`purchase-details-${p.session_id}`}>
                        Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Details modal */}
      {selected && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setSelected(null)}>
          <div className="bg-gray-800 rounded-xl border border-gray-700 w-full max-w-xl p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-white">Purchase Details</h3>
              <button onClick={() => setSelected(null)} className="text-gray-400 hover:text-white text-2xl">&times;</button>
            </div>
            <dl className="space-y-2 text-sm">
              {[
                ['Date', fmtDate(selected.created_at)],
                ['Last update', fmtDate(selected.updated_at)],
                ['User', `${selected.user_name} (${selected.user_email})`],
                ['User ID', selected.user_id],
                ['User joined', fmtDate(selected.user_joined)],
                ['Book', selected.book_title],
                ['Author', selected.book_author],
                ['Book ID', selected.book_id],
                ['Amount paid', `$${(selected.amount || 0).toFixed(2)}`],
                ['Book official price', `$${(selected.book_official_price || 0).toFixed(2)}`],
                ['Payment method', selected.payment_method],
                ['Status', selected.status],
                ['Granted by admin', selected.granted_by_admin ? 'Yes' : 'No'],
                ['Note', selected.note || '—'],
                ['Session ID', selected.session_id || '—'],
                ['PayPal capture ID', selected.paypal_capture_id || '—'],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between border-b border-gray-700 pb-2">
                  <span className="text-gray-400">{k}</span>
                  <span className="text-white text-right max-w-[60%] break-all">{v}</span>
                </div>
              ))}
            </dl>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;
